import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchemas } from '../config/masterDataConfig';
import { masterDataApi } from '../services/masterDataApi';

export const useMasterDataForm = (type, fields, initialData = null, onSubmit, onClose) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [options, setOptions] = useState({});

  const schema = validationSchemas[type];

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    reset,
    setValue,
    watch,
    trigger,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialData || { is_active: true },
    mode: 'onChange',
  });

  /**
   * Reset on edit
   */
  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  /**
   * 🔥 FETCH OPTIONS (GENERIC)
   */
  const fetchOptions = async (field, values) => {
    try {
      const params = {};

      // attach dependencies
      if (field.dependsOn) {
        field.dependsOn.forEach(dep => {
          if (values[dep]) {
            params[dep] = values[dep];
          }
        });
      }

      const res = await masterDataApi.getAll(field.model, params);

      const formatted = res.data.data.map(item => ({
        label: item.name || item.title,
        value: item.id,
      }));

      setOptions(prev => ({
        ...prev,
        [field.name]: formatted
      }));

    } catch (err) {
      console.error(`Error loading ${field.name}`, err);
    }
  };

  /**
   * 🔥 WATCH DEPENDENCIES
   */
  useEffect(() => {
    const subscription = watch((values, { name }) => {

      fields.forEach(field => {

        // if dependency changed
        if (field.dependsOn?.includes(name)) {

          // reset child
          setValue(field.name, '');

          // fetch new options
          fetchOptions(field, values);
        }

      });

    });

    return () => subscription.unsubscribe();
  }, [watch, fields]);

  /**
   * 🔥 INITIAL LOAD (important)
   */
  useEffect(() => {
    const values = watch();

    fields.forEach(field => {
      if (field.type === 'relation') {
        fetchOptions(field, values);
      }
    });

  }, []);

  /**
   * Submit
   */
  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      if (!initialData) reset();
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    register,
    handleSubmit: handleSubmit(handleFormSubmit),
    errors,
    touchedFields,
    isSubmitting,
    setValue,
    watch,
    trigger,
    control,
    options, // 👈 IMPORTANT
  };
};